import {productService} from '../services/ProductService';
import {imageService} from "@/services/ImageService";
import { toast } from "vue3-toastify";
import { i18n } from "./../lang";
import {productGroupService} from "@/services/ProductGroupService";

const state = {
    products: [],
    currentDeletingProduct: null,
    productRequest : {
        loading: false
    },
    currentProduct: null,
    addingProductGroupId: null,
    productsFetchedAt: null,
    productsStale: false,
}

let productsRequest = null;
let productsWrites = 0;

function savedProductOf(sent, returned) {
    if (!returned?.productId) return sent;
    const isOverride = sent.orgId != null && returned.orgId != null && String(returned.orgId) !== String(sent.orgId);
    if (isOverride) return sent;
    return {
        ...sent,
        ...returned,
        productGroup: returned.productGroup || sent.productGroup,
        checked: sent.checked ?? false
    };
}

const actions = {
    ensureProducts({state, dispatch}){
        if (state.products.length && !state.productsStale) return Promise.resolve(state.products);
        return dispatch('refreshProducts');
    },
    refreshProducts({dispatch}){
        if (!productsRequest) {
            productsRequest = dispatch('getProducts').finally(() => {
                productsRequest = null;
            });
        }
        return productsRequest;
    },
    getProducts({commit, dispatch}){
        const writesAtStart = productsWrites;
        commit('setProductRequest', {loading: true});
        return productService.getProducts()
            .then((response) => {
                if (productsWrites !== writesAtStart) return dispatch('getProducts');
                const productsData = response.data.map(p => ({
                    ...p,
                    checked: false
                }));
                commit('setProductRequest', {loading: false});
                commit('filterProductsList', productsData);
                commit('setProductsFetchedAt', Date.now());
            })
            .catch((error) => commit('setProductRequest', {loading: false, message: error}));
    },
    async updateProduct({dispatch, commit, state}, {product, file, orgId, extraFiles}){
        commit('setProductRequest', {loading: true});
        // Upload main image
        if (file){
            try {
                const imageUrl = await imageService.uploadImage(file, orgId);
                product.imageUrl = imageUrl;
            } catch (error) {
                console.error("Error uploading image : "+error)
                commit('setProductRequest', {loading: false, message: error})
                return false;
            }
        }

        // Upload extra images
        if (extraFiles){
            product.extraImageUrls = "";
            if (Array.isArray(extraFiles)) {
                try {
                    for (let i = 0; i < extraFiles.length; i++) {
                        const extraImageUrl = await imageService.uploadImage(extraFiles[i], orgId);
                        // Add extra image to product extraImageUrls csv string
                        product.extraImageUrls += extraImageUrl + ",";
                    }
                } catch (error) {
                    console.error("Error uploading extra images : "+error)
                    commit('setProductRequest', {loading: false, message: error})
                    return false;
                }
            } else {
                // handle single file
                try {
                    const extraImageUrl = await imageService.uploadImage(extraFiles, orgId);
                    // Add extra image to product extraImageUrls csv string
                    product.extraImageUrls += extraImageUrl + ",";
                } catch (error) {
                    console.error("Error uploading extra images : "+error)
                    commit('setProductRequest', {loading: false, message: error})
                    return false;
                }
            }
        }
        return productService.updateProduct(product)
            .then((response) => {
                toast.success(i18n.global.t("toast.successEdit"));
                commit('setProductRequest', {loading: false});
                const saved = savedProductOf(product, response.data);
                const updateProducts = state.products.map(x => (x.productId == product.productId ? saved : x));
                commit('updateProducts', updateProducts);
                commit('setCurrentProduct', null);
                return true;
            })
            .catch((error) => {
                toast.error(i18n.global.t("toast.errorEdit"));
                commit('setProductRequest', {loading: false, message: error})
                return false;
            });
    },
    async addProduct({commit, state}, {product, file, orgId, extraFiles}){
        commit('setProductRequest', {loading: true});

        // Upload main image
        if (file){
            try {
                const imageUrl = await imageService.uploadImage(file, orgId);
                product.imageUrl = imageUrl;
            } catch (error) {
                console.error("Error uploading image : "+error)
                commit('setProductRequest', {loading: false, message: error})
                return;
            }
        }

        // Upload extra images
        if (extraFiles){
            product.extraImageUrls = "";
            if (Array.isArray(extraFiles)) {
                try {
                    for (let i = 0; i < extraFiles.length; i++) {
                        const extraImageUrl = await imageService.uploadImage(extraFiles[i], orgId);
                        // Add extra image to product extraImageUrls csv string
                        product.extraImageUrls += extraImageUrl + ",";
                    }
                } catch (error) {
                    console.error("Error uploading extra images : "+error)
                    commit('setProductRequest', {loading: false, message: error})
                    return;
                }
            } else {
                // handle single file
                try {
                    const extraImageUrl = await imageService.uploadImage(extraFiles, orgId);
                    // Add extra image to product extraImageUrls csv string
                    product.extraImageUrls += extraImageUrl + ",";
                } catch (error) {
                    console.error("Error uploading extra images : "+error)
                    commit('setProductRequest', {loading: false, message: error})
                    return;
                }
            }
        }

        productService.addProduct(product)
        .then((response) => {
            toast.success(i18n.global.t("toast.createSuccess"));
            commit('setProductRequest', {loading: false});
            commit('updateProducts', [...state.products, response.data]);
            commit('setCurrentProduct', null);
        })
        .catch((error) => {
            console.error("Error adding : "+error)
            toast.error(i18n.global.t("toast.createError"));
            commit('setProductRequest', {loading: false, message: error})
        });
    },
    deleteProduct({commit, state}, product){
        commit('setProductRequest', {loading: true});
        productService.deleteProduct(product)
        .then((response) => {
            toast.success(i18n.global.t("toast.deleteSuccess"));
            commit('setProductRequest', {loading: false})
            const updateProducts = state.products.filter(function(element) {
                return element.productId !== product.productId;
            });
            commit('updateProducts', updateProducts);
            commit('setCurrentProduct', null);
            commit('setCurrentDeletingProduct', null)
        })
        .catch((error) => {
            console.error("Error deleting : "+error)
            toast.error(i18n.global.t("toast.deleteError"));
            commit('setProductRequest', {loading: false, message: error})
            commit('setCurrentDeletingProduct', null)
        })
    },
    deleteProductBatch({ commit, state }, products) {
        commit('setProductRequest', { loading: true });

        const ids = Array.isArray(products)
            ? products.map(p => (typeof p === 'object' ? p.productId : p))
            : [products].filter(Boolean);

        const cleanIds = ids
            .map(id => Number(id))
            .filter(id => !isNaN(id));

        productService.deleteProductBatch(cleanIds)
            .then(() => {
                toast.success(i18n.global.t("toast.deleteSuccess"));
                commit('setProductRequest', { loading: false });

                const idsSet = new Set(cleanIds.map(id => String(id)));
                const updatedProducts = state.products.filter(
                    element => !idsSet.has(String(element.productId))
                );

                commit('updateProducts', updatedProducts);
                commit('setCurrentProduct', null);
                commit('setCurrentDeletingProduct', null)
            })
            .catch(error => {
                console.error("Error deleting: " + error);
                toast.error(i18n.global.t("toast.deleteError"));
                commit('setProductRequest', { loading: false, message: error });
            });
    },
    updateOrderIndex({commit, state}, payload){
        commit('markProductsChanged');
        commit('productGroup/markProductGroupsChanged', null, {root: true});
        commit('setProductRequest', {loading: true});
        productGroupService.updateProductsOrderIndex(payload)
            .then(response => {
                toast.success(i18n.global.t("toast.successEdit"));
                commit('setProductRequest', {loading: false});
            })
            .catch((error) => {
                toast.error(i18n.global.t("toast.errorEdit"));
                commit('setProductRequest', {loading: false, message: error})
            });
    },
    async duplicateGroupProducts({commit, dispatch}, products) {
        commit('setProductRequest', {loading: true});
        try {
            await productService.addOrUpdateProductBatch(products);
            commit('setProductRequest', {loading: false});
            commit('markProductsChanged');
            commit('invalidateProducts');
            dispatch('refreshProducts');
            return true;
        } catch (error) {
            console.error(error);
            toast.error(i18n.global.t("toast.duplicateError"));
            commit('setProductRequest', {loading: false, message: error});
            return false;
        }
    },
    async updateProductBatch({commit, state}, updated){
        console.log('updated',updated)
        commit('setProductRequest', {loading: true});
        productService.addOrUpdateProductBatch(updated)
            .then(() => {
                toast.success(i18n.global.t("toast.successEdit"));

                const arr = Array.isArray(updated) ? updated : [];
                const updatedMap = new Map(arr.map(p => [String(p.productId), p]));

                const newProducts = state.products.map(p => {
                    const key = String(p.productId);
                    return updatedMap.has(key) ? updatedMap.get(key) : p;
                });

                commit('updateProducts', newProducts);
                commit('setProductRequest', {loading: false});
            })
            .catch((error) => {
                console.error("Error adding : " + error);
                toast.error(i18n.global.t("toast.createError"));
                commit('setProductRequest', {loading: false, message: error});
            });

    },
    duplicateProduct({commit, state}, product){
        commit('setProductRequest', {loading: true});
        const newProduct = {
            ...product,
            name: product.name + " copy",
            productBarcodes: [],
            stock: 0
        };
        productService.addProduct(newProduct)
        .then((response) => {
            toast.success(i18n.global.t("toast.duplicateSuccess"));
            commit('setProductRequest', {loading: false})
            const updateProducts = [...state.products];
            const duplicatedIndex = updateProducts.findIndex(
                p => String(p.productId) === String(product.productId)
            );

            if (duplicatedIndex >= 0) {
                updateProducts.splice(duplicatedIndex + 1, 0, response.data);
            } else {
                updateProducts.push(response.data);
            }

            commit('updateProducts', updateProducts);
            commit('setCurrentProduct', null);
        })
        .catch((error) => {
            console.error("Error deleting : "+error)
            toast.error(i18n.global.t("toast.duplicateError"));
            commit('setProductRequest', {loading: false, message: error})
        })
    },
    async changeSpecificField({commit, dispatch, state}, {product, field, boolean = false, file, orgId}) {
        const touched = ['userHidden', 'webHidden', 'kioskHidden', 'favorite', 'dynamicPrice', 'imageUrl'];
        const previous = Object.fromEntries(touched.map(key => [key, product[key]]));
        const restore = () => touched.forEach(key => { if (product[key] !== previous[key]) product[key] = previous[key]; });
        try{
            if (file) {
                const imageUrl = await imageService.uploadImage(file, orgId);
                product.imageUrl = imageUrl;
            }

            if (field === 'userHidden') {
                product.userHidden = !product.userHidden;
            }
            if (field === 'webHidden') {
                product.webHidden = !product.webHidden;
            }
            if (field === 'kioskHidden') {
                product.kioskHidden = !product.kioskHidden;
            }
            if (field === 'favorite') {
                product.favorite = !product.favorite;
            }
            if (field === 'priceVatInclOut' || field === 'priceVatInclIn') {
                product.dynamicPrice = false;
            }

            const saved = await dispatch('updateProduct', {product: product, file: null, orgId: orgId || null});
            if (!saved) restore();
            return saved;
        }catch(error){
            console.error(error);
            restore();
            commit('setProductRequest', {loading: false, message: error});
            return false;
        }
    }
}

const mutations = {
    setProductsFetchedAt(state, at){
        state.productsFetchedAt = at;
        state.productsStale = false;
    },
    invalidateProducts(state){
        state.productsStale = true;
    },
    resetProducts(state){
        state.products = [];
        state.productsFetchedAt = null;
        state.productsStale = false;
    },
    syncProductGroups(state, groups){
        const groupsById = new Map((groups || []).map(group => [String(group.productGroupId), group]));
        let changed = false;
        const products = state.products.map(product => {
            const groupId = product.productGroup?.productGroupId ?? product.productGroupId;
            const group = groupsById.get(String(groupId));
            if (!group || !product.productGroup) return product;
            changed = true;
            const {products: groupProducts, checked, ...fields} = group;
            return {...product, productGroup: {...product.productGroup, ...fields}};
        });
        if (!changed) return;
        productsWrites += 1;
        state.products = products;
    },
    markProductsChanged(){
        productsWrites += 1;
    },
    removeOptionListFromProducts(state, optionListId){
        const isRemoved = link => String(link.optionListId) === String(optionListId);
        if (!state.products.some(product => (product.allowedOptionLists || []).some(isRemoved))) return;
        productsWrites += 1;
        state.products = state.products.map(product => (product.allowedOptionLists || []).some(isRemoved)
            ? {...product, allowedOptionLists: product.allowedOptionLists.filter(link => !isRemoved(link))}
            : product);
    },
    setProductRequest(state, {loading, message}){
        state.productRequest = {
            loading, message
        };
    },
    updateProducts(state, products){
        productsWrites += 1;
        state.products = products;
    },
    filterProductsList(state, products){
        let result = products.sort(
          (a, b) =>
            a.productGroup.productGroupId - b.productGroup.productGroupId ||
            a.name.localeCompare(b.name)
        );
        state.products = result;
    },
    setCurrentProduct(state, product){
        state.currentProduct = product;
        state.addingProductGroupId = null;
        if (product === null){
            state.productRequest = {};
        }
    },
    startAddingProduct(state, productGroupId){
        state.currentProduct = {};
        state.addingProductGroupId = productGroupId;
    },
    setCurrentDeletingProduct(state, deletingProduct){
        state.currentDeletingProduct = deletingProduct;
    },

}

export const product = {
    namespaced: true,
    state,
    actions,
    mutations
}
